import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
  }),
};

export function HeroSection() {
  const handleScrollTo = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative pb-16 pt-24 sm:pb-20 sm:pt-28 lg:pb-28 lg:pt-36">
      <div className="flex flex-col items-center justify-center text-center max-w-4xl mx-auto">
        {/* ── Center copy ── */}
        <div className="flex flex-col items-center relative z-10">
          {/* Badge */}
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 backdrop-blur-sm"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#4B73FF] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#4B73FF]" />
            </span>
            <span className="text-xs font-semibold tracking-wide text-[#4B73FF]">
              AI-Powered Real Estate CRM
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            custom={0.1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mb-6 text-[2.8rem] font-extrabold leading-[1.1] tracking-tight text-white sm:text-[3.8rem] lg:text-[4.5rem]"
          >
            The Intelligence Layer <br />
            <span className="hero-shimmer-text text-transparent bg-clip-text bg-[length:200%_100%]">
              for Real Estate
            </span>
          </motion.h1>

          {/* Sub-headline */}
          <motion.p
            custom={0.2}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mb-10 max-w-2xl text-base leading-relaxed text-white/60 sm:text-[18px] mx-auto"
          >
            <span className="font-semibold text-white/90">
              One platform for Pre-Sales, Sales, and Post-Sales operations.
            </span>{" "}
            From first enquiry to booking and collections, wrap AI intelligence around your entire
            real estate lifecycle.
          </motion.p>

          {/* CTAs */}
          <motion.div
            custom={0.3}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex flex-wrap gap-4 justify-center"
          >
            <button
              onClick={() => handleScrollTo("contact")}
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#4B73FF] to-[#7c6fff] px-8 py-4 text-sm font-semibold text-white shadow-[0_4px_18px_rgba(75,115,255,0.4)] transition-all duration-200 hover:opacity-90 hover:shadow-[0_4px_24px_rgba(75,115,255,0.6)] active:scale-[0.98]"
            >
              Book a Live Demo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              onClick={() => handleScrollTo("pre-sales")}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/10 hover:border-white/20 active:scale-[0.98]"
            >
              <Play className="h-3.5 w-3.5 fill-white/80 text-white/80" />
              Explore Platform
            </button>
          </motion.div>

          {/* Social proof strip */}
          <motion.div
            custom={0.45}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3.5"
          >
            <div className="flex -space-x-2">
              {[1, 2, 3, 4].map((i) => (
                <img
                  key={i}
                  src={`https://i.pravatar.cc/40?img=${i + 10}`}
                  alt=""
                  className="h-8 w-8 rounded-full border-2 border-[#05081D] object-cover shadow-md"
                />
              ))}
            </div>
            <p className="text-[13px] text-white/50">
              <span className="font-semibold text-white/80">500+</span> real estate professionals
              trust Clozdl.ai
            </p>
          </motion.div>
        </div>
      </div>

      <style>{`
        .hero-shimmer-text {
          background-image: linear-gradient(
            90deg,
            #ffffff 0%,
            #93b4ff 25%,
            #4B73FF 50%,
            #93b4ff 75%,
            #ffffff 100%
          );
          animation: shimmerGradient 3s linear infinite;
        }
        @keyframes shimmerGradient {
          0% { background-position: 0% center; }
          100% { background-position: 200% center; }
        }
      `}</style>
    </section>
  );
}
